import React, { useState } from "react";
import {
  Table,
  Button,
  Space,
  Input,
  Select,
  Card,
  Typography,
  Tag,
  Modal,
  Form,
  Row,
  Col,
  message,
  Descriptions,
  Badge,
  Spin,
  Tooltip,
  Progress,
  Statistic,
  Alert,
  InputNumber,
  Tabs,
  List,
  Avatar,
} from "antd";
import {
  EyeOutlined,
  ReloadOutlined,
  BookOutlined,
  ClockCircleOutlined,
  UserOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { 
  customerCourseService, 
  type CustomerCourse, 
  type GetCustomerCoursesParams 
} from "../services/customerCourseService";
import moment from "moment";

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const CustomerCoursesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useState<GetCustomerCoursesParams>({
    page: 1,
    limit: 10,
  });
  const [selectedCourse, setSelectedCourse] = useState<CustomerCourse | null>(null);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [useModalVisible, setUseModalVisible] = useState(false);
  const [extendModalVisible, setExtendModalVisible] = useState(false);
  const [deactivateModalVisible, setDeactivateModalVisible] = useState(false);
  const [expiringModalVisible, setExpiringModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("1");
  const [form] = Form.useForm();
  const [extendForm] = Form.useForm();
  const [deactivateForm] = Form.useForm();

  // Query สำหรับดึงข้อมูลคอร์สลูกค้า
  const { data: coursesResponse, isLoading, refetch } = useQuery(
    ['customerCourses', searchParams],
    () => customerCourseService.getCustomerCourses(searchParams),
    {
      keepPreviousData: true,
      onError: (error: any) => {
        message.error('ບໍ່ສາມາດໂຫລດຂໍ້ມູນລູກຄ້າໄດ້');
        console.error('Error fetching customer courses:', error);
      }
    }
  );

  // Query สำหรับคอร์สที่กำลังจะหมดอายุ
  const { data: expiringCoursesData, isLoading: isExpiringLoading } = useQuery(
    ['expiringCourses'],
    () => customerCourseService.getExpiringCourses({ days: 30 }),
    {
      onError: (error: any) => {
        message.error('ບໍ່ສາມາດໂຫລດຂໍ້ມູນຄອສທີ່ກຳລັງໝົດອາຍຸໄດ້');
        console.error('Error fetching expiring courses:', error);
      }
    }
  );

  // Mutation สำหรับใช้คอร์ส
  const useCourseMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => 
      customerCourseService.useCustomerCourse(id, data),
    {
      onSuccess: () => {
        message.success('ໃຊ້ຄອສສຳເລັດ');
        queryClient.invalidateQueries(['customerCourses']);
        queryClient.invalidateQueries(['expiringCourses']);
        setUseModalVisible(false);
        setSelectedCourse(null);
        form.resetFields();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດໃຊ້ຄອສໄດ້');
      }
    }
  );

  // Mutation สำหรับขยายอายุคอร์ส
  const extendCourseMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => 
      customerCourseService.extendCustomerCourse(id, data),
    {
      onSuccess: () => {
        message.success('ເພີ່ມອາຍຸຄອສສຳເລັດ');
        queryClient.invalidateQueries(['customerCourses']);
        queryClient.invalidateQueries(['expiringCourses']);
        setExtendModalVisible(false);
        setSelectedCourse(null);
        extendForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດເພີ່ມອາຍຸຄອສໄດ້');
      }
    }
  );

  // Mutation สำหรับปิดการใช้งานคอร์ส
  const deactivateCourseMutation = useMutation(
    ({ id, data }: { id: string; data: any }) => 
      customerCourseService.deactivateCustomerCourse(id, data),
    {
      onSuccess: () => {
        message.success('ປິດການໃຊ້ງານຄອສສຳເລັດ');
        queryClient.invalidateQueries(['customerCourses']);
        setDeactivateModalVisible(false);
        setSelectedCourse(null);
        deactivateForm.resetFields();
      },
      onError: (error: any) => {
        message.error(error?.response?.data?.message || 'ບໍ່ສາມາດປິດການໃຊ້ງານຄອສໄດ້');
      }
    }
  );

  const handleViewDetails = (course: any) => {
    // แปลงข้อมูลให้ตรงกับ interface ที่คาดหวัง
    const transformedCourse = {
      ...course,
      customer: course.customerData && course.customerData[0],
      course: course.courseData && course.courseData[0],
      receipt: course.receiptData && course.receiptData[0]
    };
    setSelectedCourse(transformedCourse);
    setDetailModalVisible(true);
  };

  const handleUseCourse = (course: any) => {
    const transformedCourse = {
      ...course,
      customer: course.customerData && course.customerData[0],
      course: course.courseData && course.courseData[0],
      receipt: course.receiptData && course.receiptData[0]
    };
    setSelectedCourse(transformedCourse);
    form.setFieldsValue({ sessionsUsed: 1 });
    setUseModalVisible(true);
  };

  const handleExtendCourse = (course: any) => {
    const transformedCourse = {
      ...course,
      customer: course.customerData && course.customerData[0],
      course: course.courseData && course.courseData[0],
      receipt: course.receiptData && course.receiptData[0]
    };
    setSelectedCourse(transformedCourse);
    extendForm.setFieldsValue({ days: 30 });
    setExtendModalVisible(true);
  };

  const handleDeactivateCourse = (course: any) => {
    const transformedCourse = {
      ...course,
      customer: course.customerData && course.customerData[0],
      course: course.courseData && course.courseData[0],
      receipt: course.receiptData && course.receiptData[0]
    };
    setSelectedCourse(transformedCourse);
    setDeactivateModalVisible(true);
  };

  const handleUseSubmit = (values: any) => {
    if (!selectedCourse) return;
    useCourseMutation.mutate({ id: selectedCourse._id, data: values });
  };

  const handleExtendSubmit = (values: any) => {
    if (!selectedCourse) return;
    extendCourseMutation.mutate({ id: selectedCourse._id, data: values });
  };

  const handleDeactivateSubmit = (values: any) => {
    if (!selectedCourse) return;
    deactivateCourseMutation.mutate({ id: selectedCourse._id, data: values });
  };

  const handleSearch = (value: string) => {
    setSearchParams(prev => ({ ...prev, search: value, page: 1 }));
  };

  const handleStatusFilter = (status?: string) => {
    let filterParams: any = { page: 1 };
    
    switch (status) {
      case 'active':
        filterParams = { ...filterParams, isActive: true, isExpired: false };
        break;
      case 'expired':
        filterParams = { ...filterParams, isExpired: true };
        break;
      case 'inactive':
        filterParams = { ...filterParams, isActive: false };
        break;
      default:
        filterParams = { ...filterParams, isActive: undefined, isExpired: undefined };
    }
    
    setSearchParams(prev => ({ ...prev, ...filterParams }));
  };

  const handleTableChange = (pagination: any) => {
    setSearchParams(prev => ({
      ...prev,
      page: pagination.current,
      limit: pagination.pageSize,
    }));
  };

  const getStatusColor = (course: any) => {
    if (!course.isActive) return 'error';
    if (moment(course.expiryDate).isBefore(moment())) return 'error';
    if (course.remainingSessions === 0) return 'warning';
    if (moment(course.expiryDate).diff(moment(), 'days') <= 7) return 'warning';
    return 'success';
  };

  const getStatusText = (course: any) => {
    if (!course.isActive) return 'ປິດການໃຊ້ງານ';
    if (moment(course.expiryDate).isBefore(moment())) return 'ໝົດອາຍຸ';
    if (course.remainingSessions === 0) return 'ໃຊ້ໝົດແລ້ວ';
    if (moment(course.expiryDate).diff(moment(), 'days') <= 7) return 'ໃກ້ໝົດອາຍຸ';
    return 'ໃຊ້ງານໄດ້';
  };

  const getUsagePercentage = (course: any) => {
    return Math.round((course.usedSessions / course.totalSessions) * 100);
  };

  const getDaysUntilExpiry = (course: any) => {
    return moment(course.expiryDate).diff(moment(), 'days');
  };



  const columns = [
    {
      title: "ລູກຄ້າ",
      dataIndex: "customer",
      key: "customer",
      render: (_: any, record: any) => {
        // ข้อมูลลูกค้าอยู่ใน customerData array
        const customer = record.customerData && record.customerData[0];
        return (
          <div>
            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
              <UserOutlined style={{ marginRight: 4, color: '#1890ff' }} />
              {customer?.name || 'N/A'}
            </div>
            <div style={{ fontSize: '12px', color: '#666' }}>
              ID: {customer?.customerId || 'N/A'}
            </div>
          </div>
        );
      },
    },
    {
      title: "ຄອສ",
      dataIndex: "course",
      key: "course",
      render: (_: any, record: any) => {
        // ข้อมูลคอร์สอยู่ใน courseData array
        const course = record.courseData && record.courseData[0];
        return (
          <div>
            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'center' }}>
              <BookOutlined style={{ marginRight: 4, color: '#52c41a' }} />
              {course?.name || 'N/A'}
            </div>
            {/* <div style={{ fontSize: '12px', color: '#666' }}>
              ໝວດ: {typeof course?.category === 'string' ? course.category : course?.category?.name || 'N/A'}
            </div> */}
          </div>
        );
      },
    },
    {
      title: "ການໃຊ້ງານ",
      key: "usage",
      width: 150,
      render: (_: any, record: any) => (
        <div>
          <Progress 
            percent={getUsagePercentage(record)} 
            size="small" 
            status={record.remainingSessions === 0 ? 'exception' : 'active'}
          />
          <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
            ໃຊ້ແລ້ວ: {record.usedSessions}/{record.totalSessions} ຄັ້ງ
          </div>
          <div style={{ fontSize: '12px', color: '#666' }}>
            ຍັງເຫຼືອ: {record.remainingSessions} ຄັ້ງ
          </div>
        </div>
      ),
    },
    {
      title: "ມື້ໝົດອາຍຸ",
      dataIndex: "expiryDate",
      key: "expiryDate",
      width: 120,
      render: (date: string, record: any) => {
        const daysLeft = getDaysUntilExpiry(record);
        const isExpired = daysLeft < 0;
        
        return (
          <div>
            <div style={{ fontSize: '12px' }}>
              {moment(date).format('DD/MM/YYYY')}
            </div>
            <div style={{ 
              fontSize: '11px', 
              color: isExpired ? '#ff4d4f' : daysLeft <= 7 ? '#faad14' : '#52c41a',
              fontWeight: 500
            }}>
              {isExpired ? `เลยมา ${Math.abs(daysLeft)} ວັນ` : 
               daysLeft === 0 ? 'ໝົດອາຍຸມື້ນີ້' :
               `ເຫຼືອ ${daysLeft} ວັນ`}
            </div>
          </div>
        );
      },
      sorter: true,
    },
    {
      title: "ສະຖານະ",
      key: "status",
      width: 120,
      render: (_: any, record: any) => (
        <Badge
          status={getStatusColor(record)}
          text={getStatusText(record)}
        />
      ),
      filters: [
        { text: 'ໃຊ້ງານໄດ້', value: 'active' },
        { text: 'ໝົດອາຍຸ', value: 'expired' },
        { text: 'ປິດການໃຊ້ງານ', value: 'inactive' },
      ],
    },
    {
      title: "ວັນທີຊື້",
      dataIndex: "purchaseDate",
      key: "purchaseDate",
      width: 100,
      render: (date: string) => (
        <div style={{ fontSize: '12px' }}>
          {moment(date).format('DD/MM/YYYY')}
        </div>
      ),
      sorter: true,
    },
    {
      title: "ຈັດການ",
      key: "actions",
      width: 200,
      render: (_: any, record: any) => (
        <Space>
          <Tooltip title="ເບິ່ງລາຍລະອຽດ">
            <Button
              type="primary"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleViewDetails(record)}
            />
          </Tooltip>
          {record.isActive && record.remainingSessions > 0 && !moment(record.expiryDate).isBefore(moment()) && (
            <Tooltip title="ໃຊ້ຄອສ">
              <Button
                size="small"
                icon={<PlayCircleOutlined />}
                onClick={() => handleUseCourse(record)}
                loading={useCourseMutation.isLoading}
              />
            </Tooltip>
          )}
          {record.isActive && (
            <Tooltip title="ເພີ່ມອາຍຸໃຊ້ງານ">
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => handleExtendCourse(record)}
                loading={extendCourseMutation.isLoading}
              />
            </Tooltip>
          )}
          {record.isActive && (
            <Tooltip title="ປິດການໃຊ້ງານ">
              <Button
                danger
                size="small"
                icon={<CloseCircleOutlined />}
                onClick={() => handleDeactivateCourse(record)}
                loading={deactivateCourseMutation.isLoading}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  const courses = coursesResponse?.data?.customerCourses || [];
  const pagination = coursesResponse?.data?.pagination;
  const expiringCourses = expiringCoursesData?.data;




  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2} style={{ margin: 0 }}>
          <BookOutlined style={{ marginRight: 8 }} />
          ຈັດການຄອສລູກຄ້າ
        </Title>
        <Space>
          <Button 
            icon={<WarningOutlined />}
            onClick={() => setExpiringModalVisible(true)}
            style={{ backgroundColor: '#faad14', borderColor: '#faad14', color: 'white' }}
          >
             ຄອສໃກ້ໝົດອາຍຸ ({expiringCourses?.total || 0})
          </Button>
          <Button 
            icon={<ReloadOutlined />} 
            onClick={() => refetch()}
            loading={isLoading}
          >
            ຮີເຟຣ
          </Button>
        </Space>
      </div>

      {/* Summary Cards */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="ຄອສທັງໝົດ"
              value={pagination?.total || 0}
              prefix={<BookOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ໃຊ້ງານໄດ້"
              value={courses.filter((c:any) => c.isActive && moment(c.expiryDate).isAfter(moment()) && c.remainingSessions > 0).length}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ໃກ້ໝົດອາຍຸ"
              value={expiringCourses?.grouped.expiring7Days.length || 0}
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="ໝົດອາຍຸແລ້ວ"
              value={expiringCourses?.grouped.expiredToday.length || 0}
              prefix={<ExclamationCircleOutlined />}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={10}>
            <Input.Search
              placeholder="ຄົ້ນຫາລູກຄ້າ, ຄອສ..."
              allowClear
              style={{ width: "100%" }}
              onSearch={handleSearch}
              enterButton
            />
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Select
              placeholder="ເບິ່ງຕາມສະຖານະ"
              allowClear
              style={{ width: "100%" }}
              onChange={handleStatusFilter}
            >
              <Option value="active">ໃຊ້ງານໄດ້</Option>
              <Option value="expired">ໝົດອາຍຸ</Option>
              <Option value="inactive">ປິດການໃຊ້ງານ</Option>
            </Select>
          </Col>
        </Row>

        <Table
          columns={columns}
          dataSource={courses}
          rowKey="_id"
          loading={isLoading}
          onChange={handleTableChange}
          pagination={{
            current: pagination?.current || 1,
            pageSize: searchParams.limit || 10,
            total: pagination?.total || 0,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} จาก ${total} รายการ`,
          }}
        />
      </Card>

      {/* Course Detail Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <BookOutlined style={{ marginRight: 8 }} />
            ລາຍລະອຽດຄອສລູກຄ້າ
          </div>
        }
        open={detailModalVisible}
        onCancel={() => {
          setDetailModalVisible(false);
          setSelectedCourse(null);
        }}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            ປິດ
          </Button>
        ]}
        width={800}
      >
        {selectedCourse && (
          <div>
            <Descriptions bordered column={2} style={{ marginBottom: 24 }}>
              <Descriptions.Item label="ລູກຄ້າ">
                <div>
                  <div style={{ fontWeight: 500 }}>{selectedCourse.customer?.name || 'N/A'}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    ID: {selectedCourse.customer?.customerId || 'N/A'} | ໂທ: {selectedCourse.customer?.phone || 'N/A'}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ຄອສ">
                <div>
                  <div style={{ fontWeight: 500 }}>{selectedCourse.course?.name || 'N/A'}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    ໝວດ: {typeof selectedCourse.course?.category === 'string' ? selectedCourse.course.category : selectedCourse.course?.category?.name || 'N/A'}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ໃຊ້ແລ້ວ / ຍັງເຫຼືອ">
                <div>
                  <Text style={{ fontSize: '16px', fontWeight: 500, color: '#1890ff' }}>
                    {selectedCourse.usedSessions} / {selectedCourse.remainingSessions} ຄັ້ງ
                  </Text>
                  <Progress 
                    percent={getUsagePercentage(selectedCourse)} 
                    size="small" 
                    style={{ marginTop: 4 }}
                    status={selectedCourse.remainingSessions === 0 ? 'exception' : 'active'}
                  />
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ວັນທີຊື້">
                {moment(selectedCourse.purchaseDate).format('DD/MM/YYYY')}
              </Descriptions.Item>
              <Descriptions.Item label="ວັນທີໝົດອາຍຸ">
                <div>
                  <div>{moment(selectedCourse.expiryDate).format('DD/MM/YYYY')}</div>
                  <div style={{ 
                    fontSize: '12px', 
                    color: getDaysUntilExpiry(selectedCourse) < 0 ? '#ff4d4f' : 
                           getDaysUntilExpiry(selectedCourse) <= 7 ? '#faad14' : '#52c41a',
                    fontWeight: 500
                  }}>
                    {getDaysUntilExpiry(selectedCourse) < 0 ? 
                      `ກາຍມາ ${Math.abs(getDaysUntilExpiry(selectedCourse))} ມື້` : 
                      getDaysUntilExpiry(selectedCourse) === 0 ? 'ໝົດອາຍຸມື້ນີ້' :
                      `ເຫຼືອ ${getDaysUntilExpiry(selectedCourse)} ມື້`}
                  </div>
                </div>
              </Descriptions.Item>
              <Descriptions.Item label="ສະຖາະ">
                <Badge
                  status={getStatusColor(selectedCourse)}
                  text={getStatusText(selectedCourse)}
                />
              </Descriptions.Item>
              <Descriptions.Item label="ເລກບິນ">
                <div>
                  <Text code>{selectedCourse.receipt?.receiptNumber || 'N/A'}</Text>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedCourse.receipt?.total ? 
                      `${new Intl.NumberFormat('lo-LA').format(selectedCourse.receipt.total)} ${selectedCourse.receipt.currency}` : 
                      'N/A'}
                  </div>
                </div>
              </Descriptions.Item>
            </Descriptions>

            {/* Usage History */}
            {selectedCourse.usageHistory && selectedCourse.usageHistory.length > 0 && (
              <Card title="ປະຫວັດການໃຊ້ງານ" style={{ marginTop: 16 }}>
                <List
                  dataSource={selectedCourse.usageHistory.slice().reverse()}
                  renderItem={(usage) => (
                    <List.Item>
                      <List.Item.Meta
                        avatar={<Avatar icon={<ClockCircleOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                        title={
                          <div>
                            <span>{moment(usage.usedAt).format('DD/MM/YYYY HH:mm')}</span>
                            {usage.sessionsUsed > 0 && (
                              <Tag color="blue" style={{ marginLeft: 8 }}>
                                ໃຊ້ {usage.sessionsUsed} ຄັ້ງ
                              </Tag>
                            )}
                          </div>
                        }
                        description={
                          <div>
                            {usage.note && <div style={{ color: '#666' }}>{usage.note}</div>}
                            {usage.serviceRecord && (
                              <div style={{ fontSize: '12px', color: '#999' }}>
                                Service Record: {usage.serviceRecord.recordId}
                              </div>
                            )}
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Card>
            )}
          </div>
        )}
      </Modal>

      {/* Use Course Modal */}
      <Modal
        title="ໃຊ້ຄອສ"
        open={useModalVisible}
        onCancel={() => {
          setUseModalVisible(false);
          setSelectedCourse(null);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Spin spinning={useCourseMutation.isLoading}>
          {selectedCourse && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
              <div>ລູກຄ້າ: <Text strong>{selectedCourse.customer?.name}</Text></div>
              <div>ຄອສ: <Text strong>{selectedCourse.course?.name}</Text></div>
              <div>ຄັ້ງທີເຫຼືອ: <Text style={{ color: '#52c41a', fontWeight: 500 }}>{selectedCourse.remainingSessions} ຄັ້ງ</Text></div>
            </div>
          )}

          <Form form={form} layout="vertical" onFinish={handleUseSubmit}>
            <Form.Item
              name="sessionsUsed"
              label="ຈຳນວນຄັ້ງທີ່ໃຊ້"
              rules={[
                { required: true, message: "ກະລຸນາປ້ອນຈຳນວນຄັ້ງ" },
                { type: 'number', min: 1, message: "ຈຳນວນຄັ້ງຕ້ອງຫລາຍກວ່າ 0" },
                { 
                  type: 'number', 
                  max: selectedCourse?.remainingSessions || 1, 
                  message: `ຈຳນວນຄັ້ງຕ້ອງບໍ່ກາຍ ${selectedCourse?.remainingSessions || 1} ຄັ້ງ` 
                }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="ຈຳນວນຄັ້ງທີ່ໃຊ້"
                min={1}
                max={selectedCourse?.remainingSessions || 1}
              />
            </Form.Item>

            <Form.Item name="serviceRecordId" label="Service Record ID (ບໍ່ບັງຄັບ)">
              <Input placeholder="Service Record ID" />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={useCourseMutation.isLoading}
                >
                  ໃຊ້ຄອສ
                </Button>
                <Button
                  onClick={() => {
                    setUseModalVisible(false);
                    setSelectedCourse(null);
                    form.resetFields();
                  }}
                  disabled={useCourseMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Extend Course Modal */}
      <Modal
        title="ເພີ່ມອາຍຸໃຊ້ງານ"
        open={extendModalVisible}
        onCancel={() => {
          setExtendModalVisible(false);
          setSelectedCourse(null);
          extendForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Spin spinning={extendCourseMutation.isLoading}>
          {selectedCourse && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f6ffed', borderRadius: 6, border: '1px solid #b7eb8f' }}>
              <div>ລູກຄ້າ: <Text strong>{selectedCourse.customer?.name}</Text></div>
              <div>ຄອສ: <Text strong>{selectedCourse.course?.name}</Text></div>
              <div>ໝົດອາຍຸ: <Text>{moment(selectedCourse.expiryDate).format('DD/MM/YYYY')}</Text></div>
              <div style={{ color: getDaysUntilExpiry(selectedCourse) < 0 ? '#ff4d4f' : '#faad14' }}>
                {getDaysUntilExpiry(selectedCourse) < 0 ? 
                  `ກາຍມາ ${Math.abs(getDaysUntilExpiry(selectedCourse))} ມື້` : 
                  `ເຫຼືອ ${getDaysUntilExpiry(selectedCourse)} ມື້`}
              </div>
            </div>
          )}

          <Form form={extendForm} layout="vertical" onFinish={handleExtendSubmit}>
            <Form.Item
              name="days"
              label="ຈຳນວນມື້ທີ່ເພີ່ມ"
              rules={[
                { required: true, message: "ກະລຸນາປ້ອນຈຳນວນມື້" },
                { type: 'number', min: 1, message: "ຈຳນວນມື້ຕ້ອງຫລາຍກວ່າ 0" }
              ]}
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="ຈຳນວນມື້"
                min={1}
                addonAfter="ມື້"
              />
            </Form.Item>

            <Form.Item name="reason" label="ເຫດຜົນ">
              <TextArea 
                rows={3} 
                placeholder="ເຫດຜົນການເພີ່ມອາຍຸໃຊ້ງານ (ບໍ່ບັງຄັ້ງ)"
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item shouldUpdate>
              {({ getFieldValue }) => {
                const days = getFieldValue('days');
                const newExpiryDate = days && selectedCourse ? 
                  moment(selectedCourse.expiryDate).add(days, 'days') : null;
                
                return newExpiryDate ? (
                  <Alert
                    message={`ມື້ໝົດອາຍຸໃໝ່: ${newExpiryDate.format('DD/MM/YYYY')}`}
                    type="info"
                    showIcon
                  />
                ) : null;
              }}
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={extendCourseMutation.isLoading}
                >
                  ເພີ່ມອາຍຸ
                </Button>
                <Button
                  onClick={() => {
                    setExtendModalVisible(false);
                    setSelectedCourse(null);
                    extendForm.resetFields();
                  }}
                  disabled={extendCourseMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Deactivate Course Modal */}
      <Modal
        title="ປິດການໃຊ້ງານຄອສ"
        open={deactivateModalVisible}
        onCancel={() => {
          setDeactivateModalVisible(false);
          setSelectedCourse(null);
          deactivateForm.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Spin spinning={deactivateCourseMutation.isLoading}>
          {selectedCourse && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff2f0', borderRadius: 6, border: '1px solid #ffccc7' }}>
              <Text type="warning">
                <ExclamationCircleOutlined style={{ marginRight: 4 }} />
                ການປິດໃຊ້ງານຈະເຮັດໃຫ້ຄອສນີ້ບໍ່ສາມາດໃຊ້ໄດ້ອີກ
              </Text>
              <div style={{ marginTop: 8 }}>
                <div>ລູກຄ້າ: <Text strong>{selectedCourse.customer?.name}</Text></div>
                <div>ຄອສ: <Text strong>{selectedCourse.course?.name}</Text></div>
                <div>ຄັ້ງທີເຫຼືອ: <Text style={{ color: '#52c41a' }}>{selectedCourse.remainingSessions} ຄັ້ງ</Text></div>
              </div>
            </div>
          )}

          <Form form={deactivateForm} layout="vertical" onFinish={handleDeactivateSubmit}>
            <Form.Item
              name="reason"
              label="ເຫດຜົນການປິດໃຊ້ງານ"
              rules={[{ required: true, message: "ກະລຸນາລະບຸເຫດຜົນ" }]}
            >
              <TextArea 
                rows={4} 
                placeholder="ລະບຸເຫດຜົນການໃຊ້ງານຄອສ..."
                showCount
                maxLength={500}
              />
            </Form.Item>

            <Form.Item>
              <Space>
                <Button 
                  danger
                  type="primary" 
                  htmlType="submit"
                  loading={deactivateCourseMutation.isLoading}
                >
                  ປິດການໃຊ້ງານ
                </Button>
                <Button
                  onClick={() => {
                    setDeactivateModalVisible(false);
                    setSelectedCourse(null);
                    deactivateForm.resetFields();
                  }}
                  disabled={deactivateCourseMutation.isLoading}
                >
                  ຍົກເລິກ
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </Modal>

      {/* Expiring Courses Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <WarningOutlined style={{ marginRight: 8, color: '#faad14' }} />
            ຄອສທີ່ກຳລັງຈະໝົດອາຍຸ
          </div>
        }
        open={expiringModalVisible}
        onCancel={() => setExpiringModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setExpiringModalVisible(false)}>
            ປິດ
          </Button>
        ]}
        width={800}
      >
        <Spin spinning={isExpiringLoading}>
          {expiringCourses && (
            <Tabs activeKey={activeTab} onChange={setActiveTab}>
              <Tabs.TabPane 
                tab={
                  <span>
                    <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
                    ໝົດອາຍຸແລ້ວ ({expiringCourses.grouped.expiredToday.length})
                  </span>
                } 
                key="1"
              >
                <List
                  dataSource={expiringCourses.grouped.expiredToday}
                  renderItem={(course) => (
                    <List.Item
                      actions={[
                        <Button 
                          size="small" 
                          type="primary"
                          onClick={() => {
                            setExpiringModalVisible(false);
                            handleExtendCourse(course);
                          }}
                        >
                          ເພີ່ມອາຍຸໃຊ້ງານ
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#ff4d4f' }} />}
                        title={`${course.customer?.name} - ${course.course?.name}`}
                        description={
                          <div>
                            <div>ໝົດອາຍຸ: {moment(course.expiryDate).format('DD/MM/YYYY')} 
                              <Text type="danger" style={{ marginLeft: 8 }}>
                                (ກາຍມາ {Math.abs(getDaysUntilExpiry(course))} ມື້)
                              </Text>
                            </div>
                            <div>ເຫຼືອ: {course.remainingSessions} ຄັ້ງ</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Tabs.TabPane>

              <Tabs.TabPane 
                tab={
                  <span>
                    <ClockCircleOutlined style={{ color: '#faad14' }} />
                    ໃກ້ໝົດອາຍຸ 7 ມື້ ({expiringCourses.grouped.expiring7Days.length})
                  </span>
                } 
                key="2"
              >
                <List
                  dataSource={expiringCourses.grouped.expiring7Days}
                  renderItem={(course) => (
                    <List.Item
                      actions={[
                        <Button 
                          size="small" 
                          type="primary"
                          onClick={() => {
                            setExpiringModalVisible(false);
                            handleExtendCourse(course);
                          }}
                        >
                          ເພີ່ມອາຍຸໃຊ້ງານ
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#faad14' }} />}
                        title={`${course.customer?.name} - ${course.course?.name}`}
                        description={
                          <div>
                            <div>ໝົດອາຍຸ: {moment(course.expiryDate).format('DD/MM/YYYY')} 
                              <Text type="warning" style={{ marginLeft: 8 }}>
                                (ເຫຼືອ {getDaysUntilExpiry(course)} ມື້)
                              </Text>
                            </div>
                            <div>ເຫຼືອ: {course.remainingSessions} ຄັ້ງ</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Tabs.TabPane>

              <Tabs.TabPane 
                tab={
                  <span>
                    <CalendarOutlined style={{ color: '#1890ff' }} />
                    ໃກ້ໝົດອາຍຸ 30 ມື້ ({expiringCourses.grouped.expiring30Days.length})
                  </span>
                } 
                key="3"
              >
                <List
                  dataSource={expiringCourses.grouped.expiring30Days}
                  renderItem={(course) => (
                    <List.Item
                      actions={[
                        <Button 
                          size="small" 
                          type="primary"
                          onClick={() => {
                            setExpiringModalVisible(false);
                            handleExtendCourse(course);
                          }}
                        >
                          ເພີ່ມອາຍຸໃຊ້ງານ
                        </Button>
                      ]}
                    >
                      <List.Item.Meta
                        avatar={<Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />}
                        title={`${course.customer?.name} - ${course.course?.name}`}
                        description={
                          <div>
                            <div>ໝົດອາຍຸ: {moment(course.expiryDate).format('DD/MM/YYYY')} 
                              <Text style={{ marginLeft: 8, color: '#1890ff' }}>
                                (ເຫຼືອ {getDaysUntilExpiry(course)} ມື້)
                              </Text>
                            </div>
                            <div>ເຫຼືອ: {course.remainingSessions} ຄັ້ງ</div>
                          </div>
                        }
                      />
                    </List.Item>
                  )}
                />
              </Tabs.TabPane>
            </Tabs>
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default CustomerCoursesPage;